import { useEffect, useState } from 'react';
import { UserRole } from '@wineryxplorer/shared';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { PageContainer } from '../../components/PageContainer';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { ErrorDisplay } from '../../components/ui/ErrorDisplay';
import { Button } from '../../components/ui/Button';
import { Typography } from '../../components/ui/Typography';

const LIMIT_MIN = 0;
const LIMIT_MAX = 100;

/** Parses the field into an in-range integer, or null when it is not one. */
const parseLimit = (raw: string) => {
  if (!/^-?\d+$/.test(raw.trim())) return null;
  const n = Number(raw);
  return n >= LIMIT_MIN && n <= LIMIT_MAX ? n : null;
};

export const AdminSettings = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
  const utils = trpc.useUtils();
  const [value, setValue] = useState('');

  // getVenuePushLimit is super-admin-only, so site admins never call it.
  const { data, isLoading, error, refetch } = trpc.push.getVenuePushLimit.useQuery(undefined, {
    enabled: isSuperAdmin,
  });

  useEffect(() => {
    if (data) setValue(String(data.limit));
  }, [data]);

  const save = trpc.push.setVenuePushLimit.useMutation({
    onSuccess: () => {
      utils.push.getVenuePushLimit.invalidate();
    },
  });

  if (isSuperAdmin && isLoading) {
    return (
      <PageContainer>
        <PageLoading message="Loading settings..." />
      </PageContainer>
    );
  }

  if (isSuperAdmin && error) {
    return (
      <PageContainer>
        <ErrorDisplay
          title="Failed to load settings"
          message={error.message}
          showRetry
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  const parsed = parseLimit(value);
  const invalid = parsed === null;

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <Typography variant="heading1" as="h1">
            Settings
          </Typography>
          {isSuperAdmin && (
            <div className="flex flex-col items-end gap-1">
              <Button
                loading={save.isPending}
                disabled={invalid || save.isPending}
                onClick={() => parsed !== null && save.mutate({ limit: parsed })}
              >
                Save
              </Button>
              {invalid && (
                <p role="alert" className="text-sm text-error">
                  Enter a whole number from {LIMIT_MIN} to {LIMIT_MAX}.
                </p>
              )}
              {save.error && (
                <p role="alert" className="text-sm text-error">
                  {save.error.message}
                </p>
              )}
              {save.isSuccess && !invalid && <p className="text-sm text-gray-600">Saved.</p>}
            </div>
          )}
        </div>

        <div className="max-w-sm">
          <label htmlFor="venue-push-limit" className="block text-sm font-medium text-gray-900">
            Venue push notifications per month
          </label>
          {isSuperAdmin ? (
            <input
              id="venue-push-limit"
              type="number"
              min={LIMIT_MIN}
              max={LIMIT_MAX}
              step={1}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none"
            />
          ) : (
            <p className="mt-1 text-sm text-gray-600">Only super admins can change this.</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            How many pushes each venue may send per calendar month (UTC). 0 turns venue pushes off.
          </p>
        </div>
      </div>
    </PageContainer>
  );
};
